import { request } from '../request';
import { normalizeMenuListPage, prepareMenuTreeData } from '@/utils/menu-normalizer';
import { normalizeRoleRecord, normalizeRoleRecords } from '@/utils/role-normalizer';

/** get user list */
export function fetchGetUserList(params?: Api.SystemManage.UserSearchParams) {
  return request<Api.SystemManage.UserList>({
    url: '/systemManage/getUserList',
    method: 'get',
    params
  });
}

/** update user */
export function updateUser(data?: Api.SystemManage.UpdateUserPayload) {
  const body = pickUpdateUserPayload(data);
  return request({
    url: '/systemManage/updateUser',
    method: 'post',
    data: body
  });
}

export function pickUpdateUserPayload(data?: Api.SystemManage.UpdateUserPayload): Api.SystemManage.UpdateUserPayload {
  if (!data?.id) {
    return { id: '' };
  }

  const body: Api.SystemManage.UpdateUserPayload = { id: data.id };
  if (data.password !== undefined) body.password = data.password;
  if (data.userName !== undefined) body.userName = data.userName;
  if (data.gender !== undefined) body.gender = data.gender;
  if (data.nickName !== undefined) body.nickName = data.nickName;
  if (data.phonenumber !== undefined) body.phonenumber = data.phonenumber;
  if (data.email !== undefined) body.email = data.email;
  if (data.roles !== undefined) body.roles = data.roles;
  if (data.status !== undefined) body.status = data.status;
  if (data.adjustToken !== undefined) body.adjustToken = data.adjustToken;

  return body;
}

/** delete user */
export function deleteUser(id: number) {
  return request({
    url: '/systemManage/deleteUser',
    method: 'get',
    params: {userId: id}
  });
}

/** reset user password */
export function resetPassword(userId: string) {
  return request<null>({
    url: '/systemManage/resetPassword',
    method: 'post',
    data: { userId } satisfies Api.SystemManage.ResetPasswordPayload
  });
}

/** update user */
export function createUser(data?: Api.SystemManage.CreateUserModel) {
  const modal =  data || {};
  return request({
    url: '/systemManage/createUser',
    method: 'post',
    data: modal
  });
}

/** get role list */
export async function fetchGetRoleList(params?: Api.SystemManage.RoleSearchParams) {
  const result = await request<Api.SystemManage.RoleList>({
    url: '/systemManage/getRoleList',
    method: 'get',
    params
  });

  if (!result.data?.records?.length) {
    return result;
  }

  return {
    ...result,
    data: {
      ...result.data,
      records: normalizeRoleRecords(result.data.records)
    }
  };
}

/**
 * get all roles
 *
 * these roles are all enabled
 */
export async function fetchGetAllRoles() {
  const result = await request<Api.SystemManage.AllRole[]>({
    url: '/systemManage/getAllRoles',
    method: 'get'
  });

  if (!result.data?.length) {
    return result;
  }

  return {
    ...result,
    data: normalizeRoleRecords(result.data).map(item => ({
      id: item.id,
      name: item.name,
      roleCode: item.roleCode
    }))
  };
}

/** update role */
export function updateRole(data?: Api.SystemManage.RoleSavePayload) {
  const modal = data || {};
  return request({
    url: '/systemManage/updateRole',
    method: 'post',
    data: modal
  });
}

/** create role */
export function createRole(data?: Api.SystemManage.RoleSavePayload) {
  const modal = data || {};
  return request({
    url: '/systemManage/createRole',
    method: 'post',
    data: modal
  });
}

/** delete role */
export function deleteRole(roleId: string) {
  return request({
    url: '/systemManage/deleteRole',
    method: 'get',
    params: { roleId }
  });
}

/** get menu list */
export async function fetchGetMenuList(params?: Api.SystemManage.CommonSearchParams) {
  const result = await request<Api.SystemManage.MenuList | Record<string, unknown>>({
    url: '/systemManage/getMenuList',
    method: 'get',
    params
  });

  if (result.error || result.data == null) {
    return result;
  }

  return {
    ...result,
    data: normalizeMenuListPage(result.data)
  };
}

/** get all pages, for set home page*/ 
export function fetchGetAllPages() {
  return request<string[]>({
    url: '/systemManage/getAllPages',
    method: 'get'
  });
}

/** get all menus (flat) */
export function fetchGetAllMenus() {
  return request<Api.SystemManage.MenuTree[] | Record<string, unknown>>({
    url: '/systemManage/getAllMenus',
    method: 'get'
  });
}

/** get menu tree */
export async function fetchGetMenuTree() {
  const result = await request<Api.SystemManage.MenuTree[] | Record<string, unknown>>({
    url: '/systemManage/getMenuTree',
    method: 'get'
  });

  let tree = prepareMenuTreeData(result.data);

  if (!tree.length && !result.error) {
    const allMenusResult = await fetchGetAllMenus();
    if (!allMenusResult.error) {
      tree = prepareMenuTreeData(allMenusResult.data);
    }
  }

  if (!tree.length && !result.error) {
    const listResult = await fetchGetMenuList({ current: 1, size: 9999 });
    if (!listResult.error && listResult.data?.records?.length) {
      tree = prepareMenuTreeData(listResult.data.records);
    }
  }

  return {
    ...result,
    data: tree
  };
}

/** create menu */
export function createMenu(data: Api.SystemManage.CreateMenuPayload) {
  return request({
    url: '/systemManage/createMenu',
    method: 'post',
    data
  });
}

/** update menu */
export function updateMenu(data: Api.SystemManage.UpdateMenuPayload) {
  return request({
    url: '/systemManage/updateMenu',
    method: 'post',
    data
  });
}

/** delete menu */
export function deleteMenu(menuId: number) {
  return request({
    url: '/systemManage/deleteMenu',
    method: 'get',
    params: { menuId }
  });
}

/** update role menu permissions (backend field: roleId) */
export function updateMenuPerms(roleId: string | number, menuPermsIds: number[]) {
  const modal = { roleId, menuIds: menuPermsIds };
  return request({
    url: '/systemManage/updateRolePermissions',
    method: 'post',
    data: modal
  });
}

/** get site-level data source default */
export function fetchGetDataSourceDefault() {
  return request<string>({
    url: '/systemManage/data-source/default',
    method: 'get'
  });
}

/** update site-level data source default */
export function updateDataSourceDefault(pref: string) {
  return request({
    url: '/systemManage/data-source/default',
    method: 'put',
    data: { value: pref }
  });
}

/** update app stat permission */
export function updateUserStatPerms(userId: string, menuPermsIds: string[], channels: string[], financePerm: number) {
  const modal =  {userId, appIds: menuPermsIds, channels, financePerm};
  return request({
    url: '/systemManage/updateAppPermissions',
    method: 'post',
    data: modal
  });
}
